import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import { useMediaQuery, useTheme } from "@mui/material";
import GalleryImageViewer from "../Admin/Gallery/GalleryImageViewer";

function GallerySection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [images, setImages] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const DARK_GREEN = "#052e16";
  const GREEN = "#064e2a";
  const MID_GREEN = "#15803d";
  const SOFT_GREEN = "#f4fbf6";
  const LIGHT_GREEN = "#e8f5ec";

  useEffect(() => {
    loadGalleries();
  }, []);

  useEffect(() => {
    if (!selectedGalleryId) return;
    loadImages(selectedGalleryId);
  }, [selectedGalleryId]);

  const selectedGallery = useMemo(() => {
    return galleries.find((gallery) => gallery.id === selectedGalleryId) || null;
  }, [galleries, selectedGalleryId]);

  const loadGalleries = async () => {
    setLoadingGalleries(true);

    try {
      const res = await fetch("/gallery/public/list/");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      setGalleries(list);

      if (list.length) {
        setSelectedGalleryId(list[0].id);
      }
    } catch (error) {
      console.error(error);
      setGalleries([]);
    } finally {
      setLoadingGalleries(false);
    }
  };

  const loadImages = async (galleryId) => {
    setLoadingImages(true);

    try {
      const res = await fetch(`/gallery/public/images/${galleryId}/`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const openViewer = (index) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  if (loadingGalleries) {
    return (
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background:
            "linear-gradient(180deg, #ffffff 0%, #f4fbf6 55%, #e8f5ec 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!galleries.length) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, md: 8 },
          background:
            "linear-gradient(180deg, #ffffff 0%, #f4fbf6 48%, #e8f5ec 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 360,
            height: 360,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(6,78,42,0.12), transparent 70%)",
            top: -150,
            left: -120,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(21,128,61,0.12), transparent 70%)",
            bottom: -190,
            right: -140,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.06,
            backgroundImage:
              "linear-gradient(#064e2a 1px, transparent 1px), linear-gradient(90deg, #064e2a 1px, transparent 1px)",
            backgroundSize: "54px 54px",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            sx={{
              textAlign: "center",
              mb: 0.8,
              fontWeight: 900,
              fontSize: { xs: "0.78rem", md: "0.9rem" },
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: GREEN,
            }}
          >
            Liga Deportiva Cantonal Mejía
          </Typography>

          <Typography
            component="h1"
            sx={{
              textAlign: "center",
              mb: { xs: 1.2, md: 1.5 },
              fontWeight: 950,
              fontSize: { xs: "1.9rem", sm: "2.4rem", md: "3rem" },
              lineHeight: 1.1,
              color: DARK_GREEN,
            }}
          >
            {selectedGallery?.name || "Galería"}
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              color: "#3f5f4b",
              fontWeight: 600,
              fontSize: { xs: ".95rem", md: "1.05rem" },
              mb: { xs: 2.6, md: 3.2 },
            }}
          >
            Explora cada galería y revive los mejores momentos del torneo de fútbol
          </Typography>

          <Box
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(6,78,42,0.14)",
              backgroundColor: "#fff",
              p: { xs: 1.2, sm: 1.5 },
              mb: { xs: 3, md: 4 },
              boxShadow: "0 18px 44px rgba(6,78,42,0.10)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mb: 1.2,
                px: 0.4,
              }}
            >
              <PhotoLibraryRoundedIcon
                sx={{ color: GREEN, fontSize: 20 }}
              />
              <Typography
                sx={{
                  fontWeight: 900,
                  color: DARK_GREEN,
                  fontSize: ".95rem",
                }}
              >
                Selecciona una galería
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.2,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(6,78,42,0.22)",
                  borderRadius: "999px",
                },
              }}
            >
              {galleries.map((gallery) => {
                const active = selectedGalleryId === gallery.id;

                return (
                  <Button
                    key={gallery.id}
                    onClick={() => setSelectedGalleryId(gallery.id)}
                    variant={active ? "contained" : "outlined"}
                    sx={{
                      minWidth: { xs: 140, sm: 170 },
                      height: 46,
                      borderRadius: "14px",
                      px: 2.2,
                      flexShrink: 0,
                      fontWeight: 900,
                      textTransform: "none",
                      borderColor: active ? GREEN : "rgba(6,78,42,0.22)",
                      color: active ? "#fff" : GREEN,
                      backgroundColor: active ? GREEN : "#fff",
                      whiteSpace: "nowrap",
                      boxShadow: active
                        ? "0 10px 24px rgba(6,78,42,0.22)"
                        : "none",
                      "&:hover": {
                        backgroundColor: active ? DARK_GREEN : SOFT_GREEN,
                        borderColor: DARK_GREEN,
                        color: active ? "#fff" : DARK_GREEN,
                      },
                    }}
                  >
                    {gallery.name}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {loadingImages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: GREEN }} />
            </Box>
          ) : !images.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Typography
                sx={{
                  color: "#3f5f4b",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                No hay imágenes disponibles en esta galería.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box
              sx={{
                columnCount: { xs: 2, sm: 2 },
                columnGap: "8px",
              }}
            >
              {images.map((img, index) => (
                <Box
                  key={img.id}
                  onClick={() => openViewer(index)}
                  sx={{
                    position: "relative",
                    mb: 1,
                    borderRadius: "16px",
                    overflow: "hidden",
                    cursor: "zoom-in",
                    breakInside: "avoid",
                    backgroundColor: LIGHT_GREEN,
                    border: "1px solid rgba(6,78,42,0.10)",
                    boxShadow: "0 12px 30px rgba(6,78,42,0.16)",
                  }}
                >
                  <Box
                    component="img"
                    src={img.image}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />

                  {index === 0 && (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 48%, rgba(5,46,22,0.52) 100%)",
                          pointerEvents: "none",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: 10,
                          bottom: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.7,
                          px: 1.2,
                          py: 0.7,
                          borderRadius: "999px",
                          backgroundColor: "rgba(255,255,255,0.18)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                          pointerEvents: "none",
                        }}
                      >
                        <TouchAppRoundedIcon
                          sx={{ fontSize: 18, color: "#fff" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            lineHeight: 1,
                            letterSpacing: "0.01em",
                            color: "#fff",
                          }}
                        >
                          Toca para ampliar
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="row g-3 justify-content-center">
              {images.map((img, index) => (
                <Box key={img.id} className="col-6 col-md-4 col-lg-3">
                  <Box
                    onClick={() => openViewer(index)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "18px",
                      cursor: "zoom-in",
                      aspectRatio: "1 / 1",
                      backgroundColor: LIGHT_GREEN,
                      border: "1px solid rgba(6,78,42,0.10)",
                      boxShadow: "0 14px 34px rgba(6,78,42,0.13)",
                      transition:
                        "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 20px 46px rgba(6,78,42,0.20)",
                      },
                      "&:hover .gallery-page-image": {
                        transform: "scale(1.06)",
                      },
                      "&:hover .gallery-zoom-overlay": {
                        opacity: 1,
                      },
                      "&:hover .gallery-zoom-badge": {
                        transform: "translateY(0) scale(1)",
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.image}
                      alt=""
                      className="gallery-page-image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.35s ease",
                      }}
                    />

                    <Box
                      className="gallery-zoom-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(5,46,22,0.02) 0%, rgba(5,46,22,0.52) 100%)",
                        opacity: 0,
                        transition: "opacity 0.25s ease",
                      }}
                    />

                    <Box
                      className="gallery-zoom-badge"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transform: "translateY(10px) scale(0.96)",
                        transition: "all 0.25s ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.18)",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 14px 30px rgba(0,0,0,0.20)",
                          border: "1px solid rgba(255,255,255,0.18)",
                        }}
                      >
                        <ZoomInRoundedIcon
                          sx={{ fontSize: 30, color: "#fff" }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      <GalleryImageViewer
        open={viewerOpen}
        images={images}
        currentIndex={currentIndex}
        onClose={closeViewer}
        onChangeIndex={setCurrentIndex}
        galleryName={selectedGallery?.name || "Galería"}
      />
    </>
  );
}

export default GallerySection;